
    import { mapState } from 'vuex';
    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
    import {
        faChevronLeft as fasChevronLeft,
    } from '@fortawesome/pro-solid-svg-icons/faChevronLeft';

    import {
        faChevronRight as fasChevronRight,
    } from '@fortawesome/pro-solid-svg-icons/faChevronRight';

    import DotIndicator from '@/components/Utils/DotIndicator';

    export default {
        name: 'Carousel',

        components: {
            FontAwesomeIcon,
            DotIndicator,
        },

        props: {
            breakpoints: {
                type: Object,
                default: () => ({}),
            },
        },

        data() {
            return {
                hammer: '',
                trackEl: '',
                wrapperEl: '',
                numberOfItems: 0,
                totalOfPages: 0,
                pageSize: 0,
                pageBaseSize: 0,
                page: 1,
                slide: {
                    perView: 3,
                    peek: 24,
                    gap: 0,
                    breakpoints: {
                        320: {
                            perView: 1,
                            peek: 16,
                        },

                        576: {
                            perView: 2,
                            peek: 16,
                        },

                        768: {
                            perView: 2,
                            peek: 32,
                        },

                        808: {
                            perView: 3,
                            peek: 32,
                        },

                        992: {
                            perView: 3,
                            peek: 64,
                        },

                        1284: {
                            perView: 4,
                            peek: 64,
                        },

                        1364: {
                            perView: 5,
                            peek: 64,
                        },

                        1920: {
                            perView: 5,
                            peek: 64,
                        },

                        2198: {
                            perView: 6,
                            peek: 64,
                        },

                        2478: {
                            perView: 7,
                            peek: 64,
                        },

                        3000: {
                            perView: 8,
                            peek: 64,
                        },
                    },
                },
                navItems: 0,
                icons: {
                    fasChevronLeft,
                    fasChevronRight,
                },
            };
        },

        computed: {
            ...mapState({
                $_appInfo: state => state.appInfo,
            }),

            hasPrev() {
                return this.page > 1;
            },

            hasNext() {
                return this.page && this.numberOfItems > this.slide.perView;
            },
        },

        watch: {
            '$_appInfo.isSidebarOpen'() {
                this.reScroll();
            },

            hasNext(next) {
                if (next) {
                    this.initHammer();
                }
            },
        },

        created() {
            const hasBreakpoints = Object.keys(this.breakpoints).length > 0;
            this.slide.breakpoints = hasBreakpoints ? this.breakpoints : this.slide.breakpoints;
        },

        mounted() {
            this.trackEl = this.$refs.track;
            this.wrapperEl = this.$refs.wrapper;
            this.initSlide();
        },

        methods: {
            matchBreakpoint(needle, haystack) {
                return haystack.reduce((a, b) => {
                    if (needle <= a) {
                        return a;
                    }

                    if (needle > a && needle < b) {
                        return a;
                    }

                    if (needle >= b) {
                        return b;
                    }
                });
            },

            getBreakpoint() {
                const container = this.trackEl;
                const containerWidth = container.offsetWidth;
                const breakpoints = Object.keys(this.slide.breakpoints);
                const matchBreakpoint = this.matchBreakpoint(containerWidth, breakpoints);
                return this.slide.breakpoints[matchBreakpoint];
            },

            initSlide() {
                this.$nextTick(() => {
                    // Get main elements
                    this.numberOfItems = this.wrapperEl.childElementCount;
                    this.slide.perView = 1;

                    // Get target breakpoint
                    this.slide.perView = this.getBreakpoint().perView;
                    this.slide.peek = this.getBreakpoint().peek;

                    // Calculate peek
                    const peekBefore = this.slide.peek;
                    const peekAfter = this.slide.peek;
                    const peekTotal = peekBefore + peekAfter;

                    // Calculate slides, widths and margins
                    const trackElWidth = this.trackEl.offsetWidth - peekTotal;
                    const trackElTotalMargins = (this.slide.perView - 1) * this.slide.gap + (this.slide.gap / 2);

                    const cardWidth = (trackElWidth - trackElTotalMargins) / this.slide.perView;
                    const totalMarginSize = (this.numberOfItems - 1) * this.slide.gap;
                    const totalTrackElWidth = (cardWidth * this.numberOfItems) + totalMarginSize;

                    // Set wrapperEl properties
                    this.wrapperEl.style.width = `${totalTrackElWidth}px`;
                    this.wrapperEl.style.transform = `translateX(${this.slide.peek}px)`;

                    // Calculate page
                    this.totalOfPages = this.numberOfItems / this.slide.perView;
                    this.pageBaseSize = trackElWidth + (this.slide.gap / 2);
                });
            },

            next() {
                // Set page size
                if (this.page <= 1) {
                    this.pageSize = (this.pageBaseSize - this.slide.peek - (this.slide.gap / 4)) + (this.slide.gap / 4);
                } else {
                    this.pageSize += this.pageBaseSize;
                }

                // Set translateX
                let translateX = '';
                let isLastPage = false;
                if (this.page <= 1) {
                    translateX = [`-${this.pageSize}px`, this.slide.peek];
                } else if (this.page >= this.totalOfPages) {
                    isLastPage = true;
                    translateX = this.slide.peek;
                } else {
                    translateX = `-${this.pageSize}px`;
                }

                this.$velocity(this.wrapperEl, { translateX }, { easing: 'easeInOutQuart', duration: 600 });

                this.page = isLastPage ? 1 : this.page + 1;
            },

            prev() {
                if (this.page <= 1) {
                    return false;
                }

                // Set page size
                if (this.page >= 2) {
                    this.pageSize -= this.pageBaseSize;
                }

                // Set translateX
                let translateX = '';
                let isFirstPage = false;
                if (this.page <= 2) {
                    isFirstPage = true;
                    translateX = this.slide.peek;
                } else {
                    translateX = `-${this.pageSize}px`;
                }

                this.$velocity(this.wrapperEl, { translateX }, { easing: 'easeInOutQuart', duration: 600 });

                this.page = isFirstPage ? 1 : this.page - 1;
            },

            reScroll() {
                if (this.page <= 1) {
                    return false;
                }
                const translateX = `-${this.pageSize}px`;
                this.$velocity(this.wrapperEl, { translateX }, { easing: 'easeInOutQuart', duration: 600 });
            },

            initHammer() {
                const Hammer = require('hammerjs');
                this.hammer = new Hammer(this.trackEl);

                this.hammer.on('swipeleft', () => {
                    this.next();
                });

                this.hammer.on('swiperight', () => {
                    this.prev();
                });
            },

            onWindowResize() {
                this.initSlide();
            },
        },
    };
